import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useMatches } from "react-router-dom";
import { Box, Button, Stack, type Theme, Typography } from "@mui/material";

import { PageAreaModals } from "@/apps/admin/components/Layout/PageAreaModals";
import { PAGE_SCROLLER_ID } from "@/apps/admin/components/Layout/pageScroller";
import TopNav from "@/apps/admin/components/TopNav/TopNav";
import { paths } from "@/apps/admin/configurations/router";
import { injectIntoStore } from "@/apps/admin/configurations/store";
import { OBJECT_NAME } from "@/apps/admin/enums/objectName";
import { OBJECT_TYPE } from "@/apps/admin/enums/objectType";
import {
  selectPermissions,
  selectPermissionsStatus,
  signout,
} from "@/apps/admin/features/authentication/authenticationSlice";
import {
  ADMIN_GENIE_DOCKED_INSET,
  ADMIN_PAGE_MIN_WIDTH,
} from "@/apps/admin/genie/adminGenieDock";
import { GeniePageStateProvider } from "@/apps/admin/genie/geniePageState";
import { useAdminDispatch } from "@/apps/admin/hooks/useAdminDispatch";
import { checkGlobalManagementRolePermission } from "@/apps/admin/utilities/data";
import { isRecord } from "@/apps/admin/utilities/typeGuard";
import { AskGenieButtonView } from "@/common/genie/components/AskGenieButtonGlobal/AskGenieButtonView";
import { genieLauncherPill } from "@/common/genie/components/AskGenieButtonGlobal/GenieLauncherPill";
import { ASK_GENIE_RESIZING_ATTRIBUTE } from "@/common/genie/dock/genieDockLayout";
import { useCanShowGenieSideBySide } from "@/common/genie/dock/useCanShowGenieSideBySide";
import { useOpenGenieShortcut } from "@/common/genie/hooks/useOpenGenieShortcut";
import { useClassification } from "@/state/hooks/useClassification";
import { SomethingWentWrong } from "@/ui/components/SomethingWentWrong/SomethingWentWrong";

// GEN5-5269 NEXT C1: admin runs layout's Genie dialog with its own host,
// lazy: its chunk (and the shell's slices) load when it first renders, not at
// boot.
const AdminGenie = lazy(() =>
  Promise.all([
    import("@/apps/admin/genie/AdminGenie"),
    import("@/apps/admin/genie/genieStore"),
  ]).then(([genie, genieStore]) => {
    // The shell's slices join the store with the chunk, not at boot.
    injectIntoStore(genieStore.GENIE_REDUCERS);
    return { default: genie.AdminGenie };
  })
);

// A route's `handle` is whatever the router config gave it; the pages' titles are strings
const readRouteTitle = (handle: unknown): string | undefined =>
  isRecord(handle) && typeof handle.title === "string"
    ? handle.title
    : undefined;

const GlobalLayout = () => {
  // The global master data, also on :organizationId pages (admin edits orgs, lists stay global).
  useClassification({ organizationScoped: false });
  // Genie (GEN5-5185): the top bar button toggles it, ⌘K opens it.
  const [isGenieOpen, setIsGenieOpen] = useState(false);
  const [isGenieMaximized, setIsGenieMaximized] = useState(false);
  const openGenie = useCallback(() => setIsGenieOpen(true), []);
  const toggleGenie = useCallback(
    () => setIsGenieOpen((isOpen) => !isOpen),
    []
  );
  const toggleGenieMaximized = useCallback(
    () => setIsGenieMaximized((isMaximized) => !isMaximized),
    []
  );
  useOpenGenieShortcut({ onOpen: openGenie });
  // A docked Genie sits beside the page rather than over it, so both can be
  // used at once. Maximised, or without room for both, it covers the page.
  const canDockGenie = useCanShowGenieSideBySide(ADMIN_PAGE_MIN_WIDTH);
  const genieInset =
    isGenieOpen && !isGenieMaximized && canDockGenie
      ? ADMIN_GENIE_DOCKED_INSET
      : "0px";
  const insetSx = (theme: Theme) => ({
    marginRight: genieInset,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // A drag moves the page with the panel, not after it.
    [`html[${ASK_GENIE_RESIZING_ATTRIBUTE}] &`]: { transition: "none" },
  });
  // The deepest matched route with a title names the page; the index route is home
  const pageTitle =
    useMatches()
      .map((match) => readRouteTitle(match.handle))
      .filter(Boolean)
      .pop() ?? paths.ROOT.title;
  const permissionsStatus = useSelector(selectPermissionsStatus);
  const userPermissions = useSelector(selectPermissions);
  const hasManagementPermission = checkGlobalManagementRolePermission(
    userPermissions,
    OBJECT_TYPE.MANAGEMENT,
    OBJECT_NAME.READ
  );

  useEffect(() => {
    document.title = `${pageTitle} - Genfive`;
  }, [pageTitle]);

  if (permissionsStatus === "loading") {
    return null;
  }

  if (permissionsStatus === "error") {
    return <PermissionsLoadFailed />;
  }

  if (!hasManagementPermission) {
    return <AccessDenied />;
  }

  return (
    // Fixed-position page elements (floating actions, pinned bars) and every
    // page dialog read the room Genie takes from a variable, since they ignore
    // the margin below: nothing on the page sits under a docked Genie.
    <GeniePageStateProvider>
      <PageAreaModals inset={genieInset} isGenieOpen={isGenieOpen}>
        <TopNav
          rightInset={genieInset}
          genieLauncher={
            // Every admin here may ask Genie: the page is gated on management
            // read above.
            <AskGenieButtonView
              hasAccess
              variant={genieLauncherPill({
                isOpen: isGenieOpen,
                onClick: toggleGenie,
                dataTestId: "adminGenieLauncher",
              })}
            />
          }
        />
        {/* The page scrolls in its own box (pageScroller.ts), full height
            under the fixed top bar, so its scrollbar ends where Genie starts. */}
        <Box
          id={PAGE_SCROLLER_ID}
          sx={(theme) => ({
            ...insetSx(theme),
            height: "100vh",
            overflow: "auto",
          })}
        >
          {/* Found in P7 (P7.md): this spacer was <Box height={(theme) =>
              theme.mixins.toolbar} />, but `height` cannot take the toolbar's style object, so
              MUI drew no style and the box has always been empty. Kept empty; giving it the
              toolbar's height moves every page down. */}
          <Box />
          <Box minWidth={ADMIN_PAGE_MIN_WIDTH}>
            <Outlet />
          </Box>
        </Box>
        <Suspense fallback={null}>
          <AdminGenie
            isOpen={isGenieOpen}
            onClose={() => setIsGenieOpen(false)}
            isMaximized={isGenieMaximized}
            onToggleMaximized={toggleGenieMaximized}
          />
        </Suspense>
      </PageAreaModals>
    </GeniePageStateProvider>
  );
};

export default GlobalLayout;

const AccessDenied = () => {
  const dispatch = useAdminDispatch();
  return (
    <Stack gap={2} alignItems="center">
      <Typography variant="h6">You are not allowed to see this page</Typography>
      <Box>
        <Button
          variant="contained"
          color="error"
          onClick={() => dispatch(signout())}
        >
          Log out
        </Button>
      </Box>
    </Stack>
  );
};

const PermissionsLoadFailed = () => {
  const dispatch = useAdminDispatch();
  return (
    <Stack gap={1} alignItems="center">
      <SomethingWentWrong
        title="We couldn't load your permissions"
        message="This is not an access problem. Please try again."
      />
      <Button
        variant="text"
        onClick={() => dispatch(signout())}
        data-testid="permissionsLoadFailedSignOut"
      >
        Log out
      </Button>
    </Stack>
  );
};
